<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, useSlots } from 'vue';

import KgrModal from './KgrModal.vue';
import KgrModalContent from './KgrModalContent.vue';
import KgrModalFooter from './KgrModalFooter.vue';
import KgrModalHeader from './KgrModalHeader.vue';

const props = withDefaults(
    defineProps<{
        src: string
        aspectRatio?: number
        maxWidth?: number
        loading?: boolean
        round?: boolean
    }>(),
    {
        maxWidth: 998,
        loading: false,
        round: false,
    },
);

const emit = defineEmits<{
    confirm: [blob: Blob]
    cancel: []
    // Fired when the user picks another file via the load-failed "Bild auswählen"
    // button. Lets callers that derive state from the file (e.g. a title from the
    // filename) update it; callers that don't care can ignore it.
    reselect: [file: File]
}>();

const slots = useSlots();

interface CropperSelection extends HTMLElement {
    $toCanvas: (options?: { width?: number, height?: number }) => Promise<HTMLCanvasElement>
    $change: (x: number, y: number, width: number, height: number) => void
}
const containerRef = ref<HTMLElement | null>(null);
const selectionRef = ref<CropperSelection | null>(null);
const canvasRef = ref<HTMLElement | null>(null);
const isProcessing = ref(false);
const cropperReady = ref(false);
const loadFailed = ref(false);
// The src for the <cropper-image>. Starts at the caller-owned props.src, but a
// re-select after a load failure swaps in an internally-created object URL.
const currentSrc = ref(props.src);
// Object URLs created here (via re-select) are owned by this component and must
// be revoked on unmount. props.src is owned by the caller.
const ownedUrl = ref<string | null>(null);

function nextFrame() {
    // wrap so the rAF timestamp arg isn't passed to resolve (Promise<void> expects no value)
    return new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
}

// Loads currentSrc, measures it, and initializes the cropper. If the browser
// cannot decode the image (unsupported format such as .tif, corrupt file, …)
// it switches to the load-failed state instead of showing a broken cropper —
// mirroring ngx-image-cropper's loadImageFailed behaviour in the Angular apps.
async function loadAndInit() {
    selectionRef.value?.removeEventListener('change', constrainToBounds);
    loadFailed.value = false;
    cropperReady.value = false;
    await nextTick(); // let the cropper container render before measuring it
    const loaded = await sizeContainerToImage();
    if (!loaded) {
        loadFailed.value = true;
        return;
    }
    cropperReady.value = true;
    await nextTick();
    await nextFrame();
    await nextFrame(); // let cropper-image initialize and apply its transform
    initSelection();
    selectionRef.value?.addEventListener('change', constrainToBounds);
}

onMounted(async () => {
    if (!customElements.get('cropper-canvas')) {
        await import('cropperjs');
    }
    await loadAndInit();
});

onUnmounted(() => {
    selectionRef.value?.removeEventListener('change', constrainToBounds);
    if (ownedUrl.value) {
        URL.revokeObjectURL(ownedUrl.value);
    }
});

function onReselect(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
        return;
    }
    if (ownedUrl.value) {
        URL.revokeObjectURL(ownedUrl.value);
    }
    ownedUrl.value = URL.createObjectURL(file);
    currentSrc.value = ownedUrl.value;
    emit('reselect', file);
    loadAndInit();
}

// Resolves true if the image decoded and the container was sized, false if the
// browser could not decode it (unsupported/corrupt file).
function sizeContainerToImage() {
    return new Promise<boolean>((resolve) => {
        const img = new Image();
        img.onload = () => {
            if (!containerRef.value) {
                resolve(true);
                return;
            }
            // Measure available content width by temporarily letting the container fill its parent
            // (clientWidth respects padding, matching how max-width: 100% is computed)
            containerRef.value.style.width = '100%';
            containerRef.value.style.height = '0';
            const availW = containerRef.value.clientWidth;
            const maxH = window.innerHeight * 0.6;
            const scale = Math.min(1, maxH / img.naturalHeight, availW / img.naturalWidth);
            containerRef.value.style.width = `${img.naturalWidth * scale}px`;
            containerRef.value.style.height = `${img.naturalHeight * scale}px`;
            resolve(true);
        };
        img.onerror = () => resolve(false);
        img.src = currentSrc.value;
    });
}

// sizeContainerToImage sets the container to exactly the scaled image dimensions,
// so canvas.clientWidth/Height equals the visible image area — no offset needed.
function initSelection() {
    const canvas = canvasRef.value;
    const sel = selectionRef.value;
    if (!canvas || !sel) {
        return;
    }
    const maxW = canvas.clientWidth;
    const maxH = canvas.clientHeight;
    const ratio = props.round ? 1 : props.aspectRatio;
    let w = maxW;
    let h = ratio ? w / ratio : maxH;
    if (h > maxH) {
        h = maxH;
        w = ratio ? h * ratio : w;
    }
    sel.$change((maxW - w) / 2, (maxH - h) / 2, w, h);
}

// Keep the selection inside the image. cropperjs enforces the aspect ratio
// itself (the `aspect-ratio` attribute) and preserves the centre while growing
// symmetrically, so simply rejecting an out-of-bounds change leaves the centre
// untouched and the resize stops at the edge — no re-applying, which would fight
// cropperjs's per-frame resize and cause the selection to jitter.
function constrainToBounds(event: Event) {
    const canvas = canvasRef.value;
    if (!canvas) {
        return;
    }
    const { x, y, width, height } = (event as CustomEvent).detail;
    if (x < 0 || y < 0 || x + width > canvas.clientWidth || y + height > canvas.clientHeight) {
        event.preventDefault();
    }
}

async function confirm() {
    if (!selectionRef.value || isProcessing.value || props.loading) {
        return;
    }
    isProcessing.value = true;
    try {
        const croppedCanvas = await selectionRef.value.$toCanvas({ width: props.maxWidth });
        // Fill transparent areas with white before JPEG encoding.
        // Sub-pixel floating-point gaps between image edge and selection boundary
        // would otherwise become black in the output.
        const ctx = croppedCanvas.getContext('2d');
        if (ctx) {
            ctx.globalCompositeOperation = 'destination-over';
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, croppedCanvas.width, croppedCanvas.height);
        }
        croppedCanvas.toBlob(
            (blob) => {
                if (blob) {
                    emit('confirm', blob);
                }
            },
            'image/jpeg',
            0.9,
        );
    } finally {
        isProcessing.value = false;
    }
}
</script>

<template>
    <KgrModal
        :open="true"
        full-width
        :close-on-backdrop-click="false"
    >
        <!-- Der Kopf kommt per Slot, nicht als `headline`-Prop: die drei Aufrufer portieren DREI
             verschiedene Angular-Komponenten mit je eigener Ueberschrift (editable-image „Bild
             bearbeiten", gallery-image-upload „Titel und Bildausschnitt", Profilbild). Deren
             Markup wird deshalb unveraendert durchgereicht. -->
        <KgrModalHeader
            v-if="slots.header"
            @close="emit('cancel')"
        >
            <template #headerStart>
                <div class="kgr-modal__headerContent">
                    <slot name="header"></slot>
                </div>
            </template>
        </KgrModalHeader>
        <KgrModalContent
            :scrollable="false"
            class="kgr-region--default kgr-modal__content--scrollable"
        >
            <div
                v-if="loadFailed"
                class="kgr-alert kgr-alert--error kgr-row"
            >
                {{ $t('Die Datei konnte nicht verarbeitet werden. Bitte wähle ein anderes Bild aus.') }}
            </div>
            <div v-show="!loadFailed">
                <div
                    ref="containerRef"
                    class="cropper-container kgr-row"
                >
                    <cropper-canvas
                        v-if="cropperReady"
                        ref="canvasRef"
                    >
                        <cropper-image :src="currentSrc" />
                        <cropper-shade hidden />
                        <cropper-selection
                            ref="selectionRef"
                            :aspect-ratio="round ? 1 : aspectRatio"
                            outlined
                            theme-color="rgba(0,0,0,0.85)"
                            movable
                            resizable
                            precise
                            :class="{ 'round-selection': round }"
                        >
                            <cropper-grid
                                role="grid"
                                bordered
                                covered
                                theme-color="rgba(0,0,0,0.35)"
                            />
                            <cropper-crosshair centered />
                            <cropper-handle
                                action="move"
                                theme-color="rgba(0,0,0,0.1)"
                            />
                            <cropper-handle action="n-resize" />
                            <cropper-handle action="e-resize" />
                            <cropper-handle action="s-resize" />
                            <cropper-handle action="w-resize" />
                            <cropper-handle action="ne-resize" />
                            <cropper-handle action="nw-resize" />
                            <cropper-handle action="se-resize" />
                            <cropper-handle action="sw-resize" />
                        </cropper-selection>
                    </cropper-canvas>
                </div>
                <slot></slot>
            </div>
        </KgrModalContent>
        <KgrModalFooter class="kgr-postfix kgr-postfix--gutters kgr-postfix--center">
            <button
                type="button"
                class="kgr-postfix__fixed kgr-button kgr-button--outline"
                @click="emit('cancel')"
            >
                {{ $t('Abbrechen') }}
            </button>
            <div class="kgr-postfix__fluid"></div>
            <label
                v-if="loadFailed"
                class="kgr-postfix__fixed kgr-button kgr-button--accent"
            >
                <input
                    type="file"
                    accept="image/*"
                    class="kgr-input is-hidden"
                    @change="onReselect"
                />
                {{ $t('Bild auswählen') }}
            </label>
            <button
                v-else
                type="button"
                class="kgr-postfix__fixed kgr-button kgr-button--accent"
                :disabled="isProcessing || loading"
                @click="confirm"
            >
                {{ $t('Zuschneiden') }}
            </button>
        </KgrModalFooter>
    </KgrModal>
</template>

<style scoped>
.cropper-container {
    background: #fff;
    max-height: 60vh;
    max-width: 100%;
    /* Bottom gap clears the resize handles' hit area (the handles overhang the
       canvas and their enlarged ::before target reaches ~22px below it) from any
       slot content rendered directly underneath — otherwise that content paints
       over the bottom handles and steals their clicks. */
    margin: 0 auto 28px;
}

cropper-canvas {
    display: block;
    width: 100%;
    height: 100%;
}

.round-selection {
    border-radius: 50%;
}

.round-selection cropper-grid {
    clip-path: circle(50% at center);
}

cropper-handle[action='n-resize'],
cropper-handle[action='e-resize'],
cropper-handle[action='s-resize'],
cropper-handle[action='w-resize'],
cropper-handle[action='ne-resize'],
cropper-handle[action='nw-resize'],
cropper-handle[action='se-resize'],
cropper-handle[action='sw-resize'] {
    width: 20px;
    height: 20px;
    background-color: rgba(255, 255, 255, 0.9);
    border: 2px solid rgba(0, 0, 0, 0.7);
    border-radius: 3px;
    box-sizing: border-box;
}

/* Enlarge the invisible grab target around each resize handle so it stays
   grabbable when the selection sits flush against the image edge. cropper-canvas
   clips overflow, so the part of a handle past the edge is dead — this only
   grows the hit area inward, where it's always within bounds. */
cropper-handle[action$='-resize']::before {
    content: '';
    position: absolute;
    inset: -14px;
}
</style>
